/**
 * PATH: apps/quote/src/pages/templates-page/CreateTemplateModal.tsx
 * PURPOSE: Modal for creating new spreadsheet templates
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { FileSpreadsheet, Upload, Trash2, Loader2 } from 'lucide-react'
import { quoteApi, type TemplateCategory } from '@/lib/quoteApi'

const CATEGORY_OPTIONS: { value: TemplateCategory; label: string }[] = [
  { value: 'quote', label: 'Quote' },
  { value: 'variation', label: 'Variation' },
  { value: 'claim', label: 'Payment Claim' },
  { value: 'report', label: 'Report' },
  { value: 'other', label: 'Other' },
]

interface CreateTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
}

export default function CreateTemplateModal({ isOpen, onClose, onCreated }: CreateTemplateModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<TemplateCategory>('quote')
  const [file, setFile] = useState<File | null>(null)

  const createMutation = useMutation({
    mutationFn: async () => {
      const template = await quoteApi.createTemplate(name, category, description)
      if (file && template.id) await quoteApi.uploadTemplateWorkbook(template.id, file)
      return template
    },
    onSuccess: () => {
      onCreated(); onClose()
      setName(''); setDescription(''); setCategory('quote'); setFile(null)
    },
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Create Template</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Standard Quote Template" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value as TemplateCategory)} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              {CATEGORY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the template..." rows={2} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Excel File (Optional)</label>
            <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-4 text-center">
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{file.name}</span>
                  <button onClick={() => setFile(null)} className="text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <span className="text-sm text-slate-500 dark:text-slate-400">Drop Excel file or click to upload</span>
                  <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" />
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
          <button onClick={() => createMutation.mutate()} disabled={!name || createMutation.isPending} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Create Template
          </button>
        </div>
      </div>
    </div>
  )
}
